import { useState, useRef, useEffect } from "react";
import { MessageCircleQuestion, X, Send, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { streamAI } from "@/lib/aiStream";

const SESSION_KEY = "lt_support_session";

const STARTERS = [
  "How do lead credits work?",
  "Which plan is best for me?",
  "What happens after I post a project?",
];

const VISITOR_STARTERS = [
  "Is it free for homeowners?",
  "What do pros pay per lead?",
  "How does LeadTitan work?",
];

function sessionId(userId) {
  // One thread per identity, so logging in or out never inherits someone else's chat.
  const key = `${SESSION_KEY}_${userId || "anon"}`;
  let id = localStorage.getItem(key);
  if (!id) {
    id = `sup_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
    localStorage.setItem(key, id);
  }
  return id;
}

/** Floating Claude-powered support helper. Signed-in users only. */
export default function SupportChat() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const bottomRef = useRef(null);
  const sid = useRef(null);

  useEffect(() => {
    if (!open) return;
    const id = sessionId(user?.user_id);
    sid.current = id;
    api.get("/ai/support/history", { params: { session_id: id } })
      .then((r) => setMsgs(r.data.map((m) => ({ role: m.role, text: m.text }))))
      .catch(() => setMsgs([]));
  }, [open, user?.user_id]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [msgs, busy]);

  const ask = async (question) => {
    const text = (question ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setError("");
    setBusy(true);
    setMsgs((m) => [...m, { role: "user", text }, { role: "assistant", text: "" }]);
    try {
      await streamAI("/ai/support", { session_id: sid.current, message: text }, (full) => {
        setMsgs((m) => {
          const next = [...m];
          next[next.length - 1] = { role: "assistant", text: full };
          return next;
        });
      });
    } catch (e) {
      setError(e.message || "Support is unavailable right now");
      setMsgs((m) => m.slice(0, -1));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => setOpen(!open)}
        className="fixed bottom-6 right-6 z-40 w-14 h-14 border-2 border-ink bg-acid shadow-[4px_4px_0_0_#111] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0_0_#111] transition-transform flex items-center justify-center"
        aria-label="Open help"
        data-testid="support-chat-toggle"
      >
        {open ? <X className="w-6 h-6" /> : <MessageCircleQuestion className="w-6 h-6" />}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-40 w-[min(400px,calc(100vw-3rem))] brut-card bg-white flex flex-col max-h-[70vh]" data-testid="support-chat-panel">
          <div className="border-b-2 border-ink p-4 bg-ink text-bone">
            <div className="overline text-acid">// Ask LeadTitan</div>
            <div className="font-display font-black uppercase text-lg tracking-tighter mt-1">Help desk</div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3">
            {msgs.length === 0 && (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {user
                    ? "Ask anything about credits, plans, disputes or how the platform works."
                    : "Ask anything about pricing, lead credits or how LeadTitan works — no account needed."}
                </p>
                {(user ? STARTERS : VISITOR_STARTERS).map((s) => (
                  <button key={s} onClick={() => ask(s)} className="block w-full text-left border-2 border-ink px-3 py-2 font-mono text-[11px] uppercase tracking-widest hover:bg-acid" data-testid={`support-starter-${s.slice(0, 10).replace(/\W+/g, "-").toLowerCase()}`}>
                    {s}
                  </button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => (
              <div key={i} className={`border-2 border-ink p-3 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-klein text-white ml-8" : "bg-bone mr-4"}`} data-testid={`support-msg-${m.role}-${i}`}>
                {m.text || (busy && i === msgs.length - 1 ? "..." : "")}
              </div>
            ))}
            {error && <div className="border-2 border-ink bg-white p-3 font-mono text-[11px]" data-testid="support-error">{error}</div>}
            <div ref={bottomRef} />
          </div>

          <div className="border-t-2 border-ink p-3 flex gap-2">
            <input
              className="brut-input flex-1 text-sm"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && ask()}
              placeholder="Type your question..."
              disabled={busy}
              data-testid="support-input"
            />
            <button onClick={() => ask()} disabled={busy || !input.trim()} className="brut-btn-primary px-4 disabled:opacity-40" data-testid="support-send">
              {busy ? <Sparkles className="w-4 h-4 animate-pulse" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
    </>
  );
}
